/**
 * Stories API — post a photo story with mentions of scraped-pool users.
 *
 * A one-shot tool (not a background campaign). Mass-tagging strangers reads as
 * spam, so the mention count is capped and the UI warns about ban risk.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, apiRequest, type ApiError } from "./client";

// ============================================================
// Types
// ============================================================

export interface StoryLog {
  id: number;
  account_id: number | null;
  media_path: string | null;
  caption: string;
  mentioned_count: number;
  status: string;
  error_text: string | null;
  story_id: number | null;
  created_at: string;
}

export interface StoryPostRequest {
  account_id: number;
  media_path: string;
  caption?: string;
  mention_count?: number;
}

export interface StoryPostResponse {
  ok: boolean;
  story_id: number | null;
  mentioned_count: number;
  log: StoryLog;
}

// ============================================================
// Media upload (reuses the templates media endpoint)
// ============================================================

export async function uploadStoryMedia(file: File): Promise<{ path: string; size: number }> {
  const fd = new FormData();
  fd.append("file", file);
  return apiRequest<{ path: string; size: number }>("/templates/media", {
    method: "POST",
    body: fd,
    isFormData: true,
  });
}

// ============================================================
// Queries
// ============================================================

export function useStories() {
  return useQuery<StoryLog[], ApiError>({
    queryKey: ["stories"],
    queryFn: () => api.get<StoryLog[]>("/stories"),
    staleTime: 10_000,
  });
}

// ============================================================
// Mutations
// ============================================================

export function usePostStory() {
  const qc = useQueryClient();
  return useMutation<StoryPostResponse, ApiError, StoryPostRequest>({
    mutationFn: (body) => api.post<StoryPostResponse>("/stories/post", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["stories"] }),
  });
}
